<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { vOnClickOutside } from '@vueuse/components';
import AuthAPI from 'dashboard/api/auth';
import { LocalStorage } from 'shared/helpers/localStorage';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { setColorTheme } from 'dashboard/helper/themeHelper.js';

const store = useStore();
const menuOpen = ref(false);

function toggleMenu() { menuOpen.value = !menuOpen.value; }
function closeMenu() { menuOpen.value = false; }

// Subtitulo "INICIO · {nombre sucursal}" lee del store branches
const activeBranch = computed(() => store.getters['branches/getActiveBranch']);

// ── Theme toggle (light → dark → auto) ──────────────────────
// Misma lógica que ThemeToggleButton del sidebar · global a toda la app.
const themeMode = ref(LocalStorage.get(LOCAL_STORAGE_KEYS.COLOR_SCHEME) || 'auto');
onMounted(() => {
  themeMode.value = LocalStorage.get(LOCAL_STORAGE_KEYS.COLOR_SCHEME) || 'auto';
  store.dispatch('branches/get');
});
const themeIcon = computed(() => ({
  light: 'i-lucide-sun',
  dark: 'i-lucide-moon',
  auto: 'i-lucide-monitor-cog',
}[themeMode.value]));
const themeLabel = computed(() => ({
  light: 'Claro',
  dark: 'Oscuro',
  auto: 'Auto',
}[themeMode.value]));
function cycleTheme() {
  const order = ['light', 'dark', 'auto'];
  const idx = order.indexOf(themeMode.value);
  const next = order[(idx + 1) % order.length];
  themeMode.value = next;
  LocalStorage.set(LOCAL_STORAGE_KEYS.COLOR_SCHEME, next);
  const isOSOnDarkMode = window.matchMedia('(prefers-color-scheme: dark)').matches;
  setColorTheme(isOSOnDarkMode);
}

async function handleLogout() {
  try {
    await AuthAPI.logout();
  } catch (_) {
    /* aún si la API falla, limpiamos cookies abajo en redirect */
  }
  // Redirect a la pantalla de login (Chatwoot estándar).
  window.location.href = '/app/login';
}

function goProfile() {
  closeMenu();
  const accountId = store.getters.getCurrentAccountId;
  window.location.href = `/app/accounts/${accountId}/profile/settings`;
}

function goNotifications() {
  closeMenu();
  const accountId = store.getters.getCurrentAccountId;
  window.location.href = `/app/accounts/${accountId}/notifications`;
}

function goAppearance() {
  closeMenu();
  const accountId = store.getters.getCurrentAccountId;
  window.location.href = `/app/accounts/${accountId}/profile/settings`;
}
</script>

<template>
  <header
    class="h-[76px] flex-shrink-0 flex items-center justify-between px-5 border-b z-20"
    style="background: linear-gradient(90deg, #000 0%, #0a0a0a 50%, #000 100%); border-bottom: 1px solid rgba(251,191,36,0.2);"
  >
    <!-- Izquierda: solo título CRM + subtítulo dinámico (sucursal viene del sidebar) -->
    <div class="leading-tight">
      <div
        class="font-extrabold text-[18px] tracking-wide"
        style="background: linear-gradient(135deg,#fff,#fcd34d); -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent; color: transparent;"
      >CRM VICTOR GUZMÁN FOTOGRAFÍA</div>
      <div class="text-[11px] font-semibold tracking-wide mt-0.5" style="color: rgba(255,255,255,0.55);">
        INICIO · <span style="color:#fbbf24; font-weight:800;">{{ activeBranch?.name || '—' }}</span>
      </div>
    </div>

    <!-- Derecha: theme toggle (icono+texto) + ícono configuración (52x52) con menú -->
    <div class="relative flex items-center gap-3">
      <!-- Theme toggle global · cycle light → dark → auto -->
      <button
        type="button"
        class="h-[44px] px-4 rounded-xl flex items-center gap-2 transition text-yellow-400 hover:brightness-125"
        style="background: rgba(251,191,36,0.12); border: 1px solid rgba(251,191,36,0.35); box-shadow: 0 0 12px rgba(251,191,36,0.25);"
        :title="`Cambiar apariencia (actual: ${themeLabel})`"
        @click="cycleTheme"
      >
        <span :class="themeIcon" class="text-[20px]" />
        <span class="text-[13px] font-semibold tracking-wide">{{ themeLabel }}</span>
      </button>

      <div v-on-click-outside="closeMenu" class="relative flex items-center">
      <button
        type="button"
        class="w-[52px] h-[52px] rounded-xl flex items-center justify-center transition"
        :class="menuOpen
          ? 'text-yellow-400'
          : 'text-yellow-400 hover:brightness-125'"
        style="background: rgba(251,191,36,0.18); border: 1px solid rgba(251,191,36,0.4); box-shadow: 0 0 16px rgba(251,191,36,0.5);"
        title="Configuración"
        @click="toggleMenu"
      >
        <span class="i-lucide-settings text-[26px]" />
      </button>

      <!-- Dropdown -->
      <div
        v-if="menuOpen"
        class="absolute top-[60px] right-0 min-w-[240px] rounded-xl p-2 z-50"
        style="background: #0a0a0a; border: 1px solid rgba(251,191,36,0.4); box-shadow: 0 10px 30px rgba(251,191,36,0.1), 0 10px 30px rgba(0,0,0,0.6);"
      >
        <button
          class="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-md text-[13px] text-zinc-300 hover:text-yellow-400 hover:bg-yellow-500/10 text-left transition"
          @click="goProfile"
        >
          <span class="i-lucide-user text-[15px]" />
          <span>Mi perfil</span>
        </button>
        <button
          class="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-md text-[13px] text-zinc-300 hover:text-yellow-400 hover:bg-yellow-500/10 text-left transition"
          @click="goAppearance"
        >
          <span class="i-lucide-palette text-[15px]" />
          <span>Apariencia</span>
        </button>
        <button
          class="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-md text-[13px] text-zinc-300 hover:text-yellow-400 hover:bg-yellow-500/10 text-left transition"
          @click="goNotifications"
        >
          <span class="i-lucide-bell text-[15px]" />
          <span>Notificaciones</span>
        </button>
        <div class="h-px my-1" style="background: rgba(255,255,255,0.05);"></div>
        <button
          class="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-md text-[13px] text-rose-400 hover:text-rose-300 hover:bg-rose-500/10 text-left transition"
          @click="handleLogout"
        >
          <span class="i-lucide-log-out text-[15px]" />
          <span>Cerrar sesión</span>
        </button>
      </div>
      </div>
    </div>
  </header>
</template>
